import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Bookmark, Briefcase, ChevronRight, CircleAlert, MapPin, SlidersHorizontal } from '../components/icons';
import { Button } from '../components/Button';
import { FondAube } from '../components/FondAube';
import { FeatureHeader } from '../components/FeatureHeader';
import { OffreDetail } from '../components/OffreDetail';
import { PileOffres } from '../components/PileOffres';
import { OffresDeuxVolets } from '../components/OffresDeuxVolets';
import { useGabarit } from '../hooks/useGabarit';
import { useAffichageOffre } from '../hooks/useAffichageOffre';
import { useEcranActif } from '../hooks/useEcranActif';
import { noterEvenement } from '../services/collecte';
import { PreferencesOffresForm } from '../components/PreferencesOffresForm';
import { offresActives, preremplir } from '../config/offres';
import { CONTENU_BORNE } from '../navigation/screenOptions';
import {
  creditsOffres,
  decider,
  lirePreferences,
  listerGardees,
  rechercher,
  revaliderGardees,
  titreCvPrincipal,
  type OffreGardee,
} from '../services/offres';
import { getProfile } from '../services/profile';
import { ageAu } from '../utils/offresMineurs';
import { horsFrancais, t } from '../i18n';
import { colors, radius, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import type { Decision, Offre, PreferencesOffres } from '../types/offres';
import type { EmplacementOffre } from '../types/pilotage';
import { sourceLibelle } from '../utils/offresAffichage';
import { styleMiroir, TEXTE_LTR } from '../i18n/sens';
import { ContenuLtr } from '../components/ContenuLtr';

/**
 * Onglet « Offres ».
 *
 * Sur le canal de production, l'écran d'attente reste en place jusqu'à la soumission des lots 1 et 2
 * (voir `offresActives`). Ailleurs : critères, puis « Pour toi » (pile à swiper) et « Sauvegardées ».
 * Racine d'onglet, donc pas de flèche de retour — il n'y a pas de pile à dépiler.
 */
export function OffersScreen() {
  return offresActives() ? <OffresEcran /> : <OffresAttente />;
}

function OffresAttente() {
  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <FondAube />

      <ScrollView
        contentContainerStyle={[styles.content, CONTENU_BORNE]}
        showsVerticalScrollIndicator={false}
      >
        <FeatureHeader
          icon={Briefcase}
          accent="blue"
          title={t('offers.title')}
          subtitle={t('offers.subtitle')}
          testID="offers-header"
        />

        <View style={styles.card} testID="offers-soon">
          <Text style={styles.title}>{t('offers.soonTitle')}</Text>
          <Text style={styles.text}>{t('offers.soonBody')}</Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const PETIT_ECRAN = 760;

type Phase =
  | { nom: 'chargement' }
  | { nom: 'recherche' }
  | { nom: 'erreur' }
  | { nom: 'formulaire'; initiales: PreferencesOffres }
  | { nom: 'offres'; offres: Offre[]; epuise: boolean; partielle: boolean };

type Gardees = { etat: 'chargement' } | { etat: 'erreur' } | { etat: 'ok'; liste: OffreGardee[] };

type Segment = 'pourToi' | 'sauvegardees';

/**
 * Note qu'une offre a été vue. Un composant à part plutôt qu'un hook dans `OffresEcran` : celui-ci
 * rend le formulaire de critères par un `return` anticipé, et un hook placé après serait appelé
 * conditionnellement. Ne rend rien.
 */
function SuiviAffichage({ offre, emplacement }: { offre: Offre | null; emplacement: EmplacementOffre }) {
  useAffichageOffre(offre, emplacement, useEcranActif());
  return null;
}

async function preferencesDeDepart(): Promise<PreferencesOffres> {
  const [profil, titre] = await Promise.all([getProfile().catch(() => null), titreCvPrincipal()]);
  return preremplir(profil ?? {}, titre);
}

function OffresEcran() {
  // Sous cette hauteur (iPhone SE : 667 pt), la carte a besoin de toute la place : le sous-titre et
  // les pastilles s'effacent, la commande pour modifier les critères reste.
  const petitEcran = useWindowDimensions().height < PETIT_ECRAN;
  const { etendu } = useGabarit();
  // Offre ouverte dans le volet de droite (ordinateur) ; sans effet sur le téléphone.
  const [selCle, setSelCle] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ nom: 'chargement' });
  const [prefs, setPrefs] = useState<PreferencesOffres | null>(null);
  const [segment, setSegment] = useState<Segment>('pourToi');
  const [gardees, setGardees] = useState<Gardees>({ etat: 'chargement' });
  const [detail, setDetail] = useState<{ offre: Offre; sauvegardee: boolean } | null>(null);
  const [echecDecision, setEchecDecision] = useState(false);
  // Seul compteur de l'onglet : la préparation IA. Absent si illimité ou illisible — un compteur faux
  // serait pire qu'aucun.
  const [credits, setCredits] = useState<number | null>(null);
  const [naissance, setNaissance] = useState<{ connue: boolean; iso: string | null }>({ connue: true, iso: null });

  const lireCredits = useCallback(async () => {
    try {
      setCredits((await creditsOffres()).restantes);
    } catch {
      setCredits(null);
    }
  }, []);

  useEffect(() => {
    void lireCredits();
    void getProfile()
      .then((p) => setNaissance({ connue: !!p?.birth_date, iso: p?.birth_date ?? null }))
      .catch(() => undefined);
  }, [lireCredits]);

  const chercher = useCallback(async (courantes: PreferencesOffres | null) => {
    setPhase({ nom: 'recherche' });
    try {
      const r = await rechercher();
      if (r.etat === 'sans_preferences') {
        setPhase({ nom: 'formulaire', initiales: courantes ?? (await preferencesDeDepart()) });
      } else if (r.etat === 'erreur') {
        setPhase({ nom: 'erreur' });
      } else {
        const partielle = Object.values(r.sources).includes('erreur');
        setPhase({ nom: 'offres', offres: r.offres, epuise: r.epuise, partielle });
      }
    } catch {
      setPhase({ nom: 'erreur' });
    }
  }, []);

  const charger = useCallback(async () => {
    setPhase({ nom: 'chargement' });
    try {
      const lues = await lirePreferences();
      setPrefs(lues);
      if (lues) await chercher(lues);
      else setPhase({ nom: 'formulaire', initiales: await preferencesDeDepart() });
    } catch {
      setPhase({ nom: 'erreur' });
    }
  }, [chercher]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const chargerGardees = useCallback(async () => {
    setGardees({ etat: 'chargement' });
    try {
      await revaliderGardees();
      setGardees({ etat: 'ok', liste: await listerGardees() });
    } catch {
      setGardees({ etat: 'erreur' });
    }
  }, []);

  const choisirSegment = (s: Segment) => {
    setSegment(s);
    if (s === 'sauvegardees') void chargerGardees();
  };

  // Où se trouve la personne, pour le pilotage : même règle que l'affichage des deux volets plus bas.
  const emplacementCourant = (): EmplacementOffre =>
    segment === 'sauvegardees' ? 'sauvegardees' : etendu && phase.nom === 'offres' && phase.offres.length > 0 ? 'double_volet' : 'pile';
  const noterOuverture = (offre: Offre) =>
    noterEvenement('offres_ouverture', { objetType: 'offre', objetId: offre.cle, emplacement: emplacementCourant() });
  // Après l'enregistrement de la décision seulement : un échec n'écrit rien, comme la table.
  const noterDecision = (offre: Offre) =>
    noterEvenement('offres_decision', { objetType: 'offre', objetId: offre.cle, emplacement: emplacementCourant() });

  const choisir = async (offre: Offre, decision: Decision) => {
    const restantes = phase.nom === 'offres' ? phase.offres.filter((o) => o.cle !== offre.cle) : [];
    setPhase((p) => (p.nom === 'offres' ? { ...p, offres: p.offres.filter((o) => o.cle !== offre.cle) } : p));
    setEchecDecision(false);
    if (decision === 'interessee') {
      setDetail({ offre, sauvegardee: false });
      noterOuverture(offre);
    }
    if (decision === 'sauvegardee') setDetail((d) => (d && d.offre.cle === offre.cle ? { ...d, sauvegardee: true } : d));
    try {
      await decider(offre, decision);
      noterDecision(offre);
    } catch {
      setEchecDecision(true);
    }
    // Pile vidée : on redemande, une fois la décision enregistrée, pour que le serveur l'écarte.
    if (phase.nom === 'offres' && restantes.length === 0) await chercher(prefs);
  };

  const sauvegarderDepuisDetail = async () => {
    if (!detail) return;
    if (segment === 'pourToi') {
      await choisir(detail.offre, 'sauvegardee');
      return;
    }
    setDetail({ ...detail, sauvegardee: true });
    try {
      await decider(detail.offre, 'sauvegardee');
      noterDecision(detail.offre);
    } catch {
      setEchecDecision(true);
    }
  };

  if (phase.nom === 'formulaire') {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <FondAube />
        <View style={[styles.plein, CONTENU_BORNE]}>
          <PreferencesOffresForm
            initiales={phase.initiales}
            precedentes={prefs}
            onEnregistre={(nouvelles) => {
              setPrefs(nouvelles);
              void chercher(nouvelles);
            }}
            onRetour={() => void chercher(prefs)}
          />
        </View>
      </SafeAreaView>
    );
  }

  // La pile occupe toute la hauteur et ne défile pas : la carte s'étire jusqu'aux boutons. Les
  // autres états (chargement, erreur, vide) et « Sauvegardées » gardent une zone qui défile.
  const listeEtendu: Offre[] = !etendu
    ? []
    : segment === 'pourToi'
      ? phase.nom === 'offres'
        ? phase.offres
        : []
      : gardees.etat === 'ok'
        ? gardees.liste.map((g) => g.offre)
        : [];
  const deuxVolets = etendu && listeEtendu.length > 0;
  const affichee = deuxVolets ? (listeEtendu.find((o) => o.cle === selCle) ?? detail?.offre ?? listeEtendu[0] ?? null) : null;
  const sauvegardeeAffichee =
    affichee && detail && detail.offre.cle === affichee.cle
      ? detail.sauvegardee
      : segment === 'sauvegardees' && gardees.etat === 'ok'
        ? gardees.liste.find((g) => g.offre.cle === affichee?.cle)?.decision === 'sauvegardee'
        : false;
  const choisirDansListe = (o: Offre) => {
    setSelCle(o.cle);
    setDetail(null);
    noterOuverture(o);
  };
  const decisionListe = (o: Offre, d: Decision) => {
    if (d === 'passee') {
      const i = listeEtendu.findIndex((x) => x.cle === o.cle);
      setSelCle(listeEtendu[i + 1]?.cle ?? listeEtendu[i - 1]?.cle ?? null);
    }
    void choisir(o, d);
  };
  const sauvegarderAffichee = async () => {
    if (!affichee) return;
    if (segment === 'pourToi') {
      setDetail({ offre: affichee, sauvegardee: false });
      await choisir(affichee, 'sauvegardee');
      return;
    }
    setDetail({ offre: affichee, sauvegardee: true });
    try {
      await decider(affichee, 'sauvegardee');
      noterDecision(affichee);
    } catch {
      setEchecDecision(true);
    }
  };

  const pileVisible = segment === 'pourToi' && phase.nom === 'offres' && phase.offres.length > 0;
  const pourToi = (
    <PourToi
      phase={phase}
      onReessayer={() => void charger()}
      onModifier={() => setPhase({ nom: 'formulaire', initiales: prefs ?? preremplir({}, null) })}
      dureeChoisie={dureesChoisies(prefs).length > 0}
      onDecision={(o, d) => void choisir(o, d)}
      onVoirDetail={(o) => {
        setDetail({ offre: o, sauvegardee: false });
        noterOuverture(o);
      }}
    />
  );
  // Une offre n'est « vue » que si elle est entièrement sous les yeux : la fiche de droite en double
  // volet, la carte du dessus de la pile sans détail ouvert. La liste « Sauvegardées » du téléphone
  // n'est pas mesurée (ce sont des offres déjà vues et décidées).
  const carteVisible: Offre | null = deuxVolets
    ? affichee
    : pileVisible && detail === null && phase.nom === 'offres'
      ? (phase.offres[0] ?? null)
      : null;

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <FondAube />
      <SuiviAffichage offre={carteVisible} emplacement={emplacementCourant()} />
      <View style={[styles.ecran, CONTENU_BORNE, etendu && styles.ecranLarge]}>
        <View style={styles.enteteLigne}>
          <View style={styles.entete}>
            <Text style={styles.ecranTitre} accessibilityRole="header">
              {t('offres.titre')}
            </Text>
            {/* Honnête : le classement se fait sur les critères, pas sur la lecture du CV. */}
            {segment !== 'pourToi' ? (
              <Text style={styles.ecranSousTitre} testID="offres-sous-titre-gardees">
                {t('offres.segments.sauvegardees')}
              </Text>
            ) : petitEcran ? null : (
              <Text style={styles.ecranSousTitre} testID="offres-sous-titre">
                {t('offres.sousTitre')}
              </Text>
            )}
          </View>
          {/* En haut à droite, comme la maquette : ouvre la liste des offres sauvegardées, et la referme. */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('offres.segments.sauvegardees')}
            accessibilityState={{ selected: segment === 'sauvegardees' }}
            onPress={() => choisirSegment(segment === 'sauvegardees' ? 'pourToi' : 'sauvegardees')}
            style={[styles.favori, segment === 'sauvegardees' && styles.favoriActif]}
            testID="offres-sauvegardees"
          >
            <Bookmark
              size={20}
              color={segment === 'sauvegardees' ? colors.white : colors.night}
              fill={segment === 'sauvegardees' ? colors.white : 'none'}
            />
          </Pressable>
        </View>

        {prefs && segment === 'pourToi' ? (
          <View style={styles.criteresLigne}>
            <View style={styles.criteresDefilant}>
              <CriteresPastilles prefs={prefs} />
            </View>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('offres.modifierCriteres')}
              onPress={() => setPhase({ nom: 'formulaire', initiales: prefs })}
              style={styles.filtre}
              hitSlop={4}
            >
              <SlidersHorizontal size={16} color={colors.night} />
            </Pressable>
          </View>
        ) : null}

        {credits !== null && segment === 'pourToi' ? (
          <Text style={styles.credits} testID="offres-credits">
            {t('offres.credits', { count: credits })}
          </Text>
        ) : null}

        {echecDecision ? <Bandeau texte={t('offres.etats.decisionEchec')} /> : null}

        {deuxVolets && segment === 'pourToi' && phase.nom === 'offres' && phase.partielle ? (
          <Bandeau texte={t('offres.etats.sourcePartielle')} />
        ) : null}
        {deuxVolets ? (
          <OffresDeuxVolets
            offres={listeEtendu}
            affichee={affichee}
            onChoisir={choisirDansListe}
            onDecision={segment === 'pourToi' ? decisionListe : undefined}
            detail={{
              sauvegardee: sauvegardeeAffichee,
              onSauvegarder: () => void sauvegarderAffichee(),
              age: ageAu(naissance.iso, new Date()),
              naissanceConnue: naissance.connue,
              onNaissance: (iso) => setNaissance({ connue: true, iso }),
              onDossierPret: () => void lireCredits(),
            }}
          />
        ) : pileVisible ? (
          pourToi
        ) : (
          <ScrollView contentContainerStyle={styles.defilement} showsVerticalScrollIndicator={false}>
            {segment === 'pourToi' ? (
              pourToi
            ) : (
              <ListeGardees
                gardees={gardees}
                onReessayer={() => void chargerGardees()}
                onOuvrir={(g) => {
                  setDetail({ offre: g.offre, sauvegardee: g.decision === 'sauvegardee' });
                  noterOuverture(g.offre);
                }}
              />
            )}
          </ScrollView>
        )}
      </View>

      {deuxVolets ? null : <OffreDetail
        offre={detail?.offre ?? null}
        sauvegardee={detail?.sauvegardee ?? false}
        onFermer={() => setDetail(null)}
        onSauvegarder={() => void sauvegarderDepuisDetail()}
        age={ageAu(naissance.iso, new Date())}
        naissanceConnue={naissance.connue}
        onNaissance={(iso) => setNaissance({ connue: true, iso })}
        onDossierPret={() => void lireCredits()}
      />}
    </SafeAreaView>
  );
}

/** Durées de stage et de CDD qui filtrent la recherche : seulement celles dont le contrat est demandé. */
function dureesChoisies(prefs: PreferencesOffres | null): string[] {
  if (!prefs) return [];
  return [
    prefs.contrats.includes('stage') && prefs.dureeStageMois !== null ? t(`offres.dureesStage.${prefs.dureeStageMois}`) : null,
    prefs.contrats.includes('cdd') && prefs.dureeCdd ? t(`offres.dureesCdd.${prefs.dureeCdd}`) : null,
  ].filter((d): d is string => d !== null);
}

// Rappel des critères en cours : contrats, ville, rythme et durées en pastilles.
function CriteresPastilles({ prefs }: { prefs: PreferencesOffres }) {
  const rythme = prefs.contrats.includes('alternance') && prefs.rythme ? prefs.rythme : null;
  const durees = dureesChoisies(prefs);
  // Un seul arrêt pour le lecteur d'écran, et le rythme en toutes lettres plutôt que « 3 j barre 2 j ».
  const lu = [
    ...prefs.contrats.map((c) => t(`offres.contrats.${c}`)),
    prefs.ville,
    rythme ? t(`offres.rythmesComplets.${rythme}`) : null,
    ...durees,
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <View testID="offres-criteres" accessible accessibilityLabel={lu}>
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.pastilles}>
      {prefs.contrats.map((c) => (
        <View key={c} style={[styles.pastille, styles.pastilleContrat]}>
          <Text style={[styles.pastilleTexte, styles.pastilleContratTexte]}>{t(`offres.contrats.${c}`)}</Text>
        </View>
      ))}
      {prefs.ville ? (
        <View style={styles.pastille} testID="offres-critere-ville">
          <MapPin size={14} color={colors.night} />
          <ContenuLtr style={styles.pastilleVille}>
            <Text style={[styles.pastilleTexte, TEXTE_LTR]} numberOfLines={1}>
              {prefs.ville}
            </Text>
          </ContenuLtr>
        </View>
      ) : null}
      {rythme ? (
        <View style={styles.pastille} testID="offres-critere-rythme">
          <Text style={styles.pastilleTexte}>{t(`offres.rythmes.${rythme}`)}</Text>
        </View>
      ) : null}
      {durees.map((d) => (
        <View key={d} style={styles.pastille} testID="offres-critere-duree">
          <Text style={styles.pastilleTexte}>{d}</Text>
        </View>
      ))}
    </ScrollView>
    </View>
  );
}

function Bandeau({ texte }: { texte: string }) {
  return (
    <View style={styles.bandeau}>
      <CircleAlert size={18} color={colors.bluePrimary} />
      <Text style={styles.bandeauTexte}>{texte}</Text>
    </View>
  );
}

function Attente({ texte }: { texte: string }) {
  return (
    <View style={styles.etat}>
      <ActivityIndicator color={colors.night} />
      <Text style={styles.text}>{texte}</Text>
    </View>
  );
}

function Etat({
  titre,
  texte,
  mention,
  action,
}: {
  titre: string;
  texte: string;
  mention?: string;
  action?: { titre: string; onPress: () => void };
}) {
  return (
    <View style={[styles.card, styles.etat]}>
      <Text style={styles.title}>{titre}</Text>
      <Text style={[styles.text, styles.centre]}>{texte}</Text>
      {mention ? <Text style={[styles.text, styles.centre]} testID="offres-france-uniquement">{mention}</Text> : null}
      {action ? <Button title={action.titre} variant="secondary" onPress={action.onPress} /> : null}
    </View>
  );
}

function PourToi({
  phase,
  onReessayer,
  onModifier,
  dureeChoisie,
  onDecision,
  onVoirDetail,
}: {
  phase: Phase;
  onReessayer: () => void;
  onModifier: () => void;
  /** Une durée de stage ou de CDD filtre la recherche : la liste vide peut venir d'elle. */
  dureeChoisie: boolean;
  onDecision: (offre: Offre, decision: Decision) => void;
  onVoirDetail: (offre: Offre) => void;
}) {
  if (phase.nom === 'chargement') return <Attente texte={t('offres.preferences.chargement')} />;
  if (phase.nom === 'recherche') return <Attente texte={t('offres.etats.chargement')} />;
  if (phase.nom === 'erreur') {
    return (
      <Etat
        titre={t('offres.etats.erreurTitre')}
        texte={t('offres.etats.erreurTexte')}
        action={{ titre: t('offres.etats.reessayer'), onPress: onReessayer }}
      />
    );
  }
  if (phase.nom !== 'offres') return null;

  return (
    <View style={phase.offres.length > 0 ? styles.pourToiPile : styles.pourToi}>
      {phase.partielle ? <Bandeau texte={t('offres.etats.sourcePartielle')} /> : null}
      {phase.offres.length > 0 ? (
        <PileOffres offres={phase.offres} onDecision={onDecision} onVoirDetail={onVoirDetail} />
      ) : phase.epuise ? (
        <Etat
          titre={t('offres.etats.epuiseTitre')}
          texte={t('offres.etats.epuiseTexte')}
          mention={horsFrancais() ? t('offres.franceUniquement') : undefined}
          action={{ titre: t('offres.modifierCriteres'), onPress: onModifier }}
        />
      ) : (
        <Etat
          titre={t('offres.etats.videTitre')}
          texte={t(dureeChoisie ? 'offres.etats.videTexteDuree' : 'offres.etats.videTexte')}
          mention={horsFrancais() ? t('offres.franceUniquement') : undefined}
          action={{ titre: t('offres.modifierCriteres'), onPress: onModifier }}
        />
      )}
    </View>
  );
}

function ListeGardees({
  gardees,
  onReessayer,
  onOuvrir,
}: {
  gardees: Gardees;
  onReessayer: () => void;
  onOuvrir: (g: OffreGardee) => void;
}) {
  if (gardees.etat === 'chargement') return <Attente texte={t('offres.etats.chargement')} />;
  if (gardees.etat === 'erreur') {
    return (
      <Etat
        titre={t('offres.etats.erreurTitre')}
        texte={t('offres.etats.erreurTexte')}
        action={{ titre: t('offres.etats.reessayer'), onPress: onReessayer }}
      />
    );
  }
  if (gardees.liste.length === 0) {
    return <Etat titre={t('offres.etats.sauvegardeesVideTitre')} texte={t('offres.etats.sauvegardeesVideTexte')} />;
  }
  return (
    <View style={styles.liste}>
      {gardees.liste.map((g) => (
        <Pressable key={g.offre.cle} accessibilityRole="button" onPress={() => onOuvrir(g)} style={styles.ligne}>
          <View style={styles.ligneTextes}>
            <ContenuLtr>
              <Text style={[styles.ligneTitre, TEXTE_LTR]} numberOfLines={2}>
                {g.offre.titre}
              </Text>
            </ContenuLtr>
            <Text style={styles.ligneSous} numberOfLines={1}>
              {[g.offre.entreprise.nom, sourceLibelle(g.offre.source, g.offre.partenaire)].filter(Boolean).join(' · ')}
            </Text>
            {g.offre.retiree ? <Text style={styles.retiree}>{t('offres.etats.retiree')}</Text> : null}
          </View>
          <View style={styleMiroir()}><ChevronRight size={20} color={colors.grey500} /></View>
        </Pressable>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  credits: { fontSize: typography.small.size, fontWeight: '600', color: colors.grey500 },
  container: { flex: 1, backgroundColor: colors.grey50 },
  plein: { flex: 1 },
  content: { padding: screenPadding, gap: spacing.lg, paddingBottom: spacing.xxl },
  card: {
    gap: spacing.sm,
    padding: spacing.lg,
    borderRadius: radius.xl,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  title: { fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.night },
  text: {
    fontSize: typography.body.size,
    lineHeight: typography.body.lineHeight,
    color: colors.grey500,
  },
  centre: { textAlign: 'center' },
  enteteLigne: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.md },
  entete: { flex: 1, minWidth: 0, gap: 2 },
  // 28 pt comme la maquette : le titre ne doit pas voler la hauteur de la carte.
  ecranTitre: {
    fontSize: 28,
    lineHeight: 34,
    fontWeight: '800',
    letterSpacing: -0.6,
    color: colors.navy,
  },
  ecranSousTitre: { fontSize: typography.bodyMd.size, lineHeight: typography.bodyMd.lineHeight, color: colors.grey500 },
  // Un seul rang, qui défile s'il y a beaucoup de critères : deux rangs de pastilles prenaient 44 pt à la carte.
  criteresLigne: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm - 2 },
  criteresDefilant: { flex: 1, minWidth: 0 },
  filtre: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: colors.bordureClaire,
  },
  pastilles: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm - 2 },
  pastille: {
    minHeight: 36,
    maxWidth: '60%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: spacing.md - 2,
    borderRadius: 18,
    borderWidth: 1.5,
    borderColor: colors.bordureClaire,
    backgroundColor: colors.white,
  },
  pastilleVille: { flexShrink: 1, minWidth: 0 },
  pastilleContrat: { backgroundColor: colors.night, borderColor: colors.night },
  pastilleTexte: { flexShrink: 1, fontSize: typography.small.size, fontWeight: '600', color: colors.night },
  pastilleContratTexte: { color: colors.white, fontWeight: '700' },
  // Favori : carré arrondi à ombre douce, sans bordure, comme la maquette.
  favori: {
    width: 44,
    height: 44,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.white,
    ...shadow.card,
  },
  favoriActif: { backgroundColor: colors.night },
  bandeau: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: radius.md,
    backgroundColor: colors.infoSoft,
  },
  bandeauTexte: { flex: 1, fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.night },
  etat: { alignItems: 'center', gap: spacing.md, paddingVertical: spacing.xl },
  pourToi: { gap: spacing.lg },
  pourToiPile: { flex: 1, gap: spacing.sm, paddingTop: spacing.sm },
  ecranLarge: { paddingHorizontal: spacing.xxl + spacing.xl, paddingTop: spacing.xxl, paddingBottom: spacing.xl, gap: spacing.lg },
  ecran: { flex: 1, paddingHorizontal: screenPadding, paddingTop: screenPadding, gap: spacing.md },
  defilement: { gap: spacing.lg, paddingTop: spacing.sm, paddingBottom: spacing.xxl },
  liste: { gap: spacing.sm },
  ligne: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  ligneTextes: { flex: 1, gap: spacing.xs },
  ligneTitre: { fontSize: typography.bodyMd.size, fontWeight: '600', color: colors.night },
  ligneSous: { fontSize: typography.small.size, color: colors.grey500 },
  retiree: { fontSize: typography.small.size, fontWeight: '600', color: colors.night },
});
